import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { supabase } from "../../supabaseClient";
import { useAuth } from "../../context/AuthContext";
import { AlertBox, TournamentPageSkeleton } from "../UI/DesignStandards";
import TournamentSubpageHead from "../SEO/TournamentSubpageHead";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { addCurrency } from "../../utils/currencyUtils";
import { fetchTournamentSignupSettings, getDefaultSignupSettings, averageProofIsOffered, averageProofIsRequired, directorAverageVerificationInstructions } from "../../utility/signupSettings";
import { occupancyBySquadId } from "../../utility/signupOccupancy";
import { fetchDoublesSinglesSetup } from "../../utility/doublesSinglesSetupApi";
import {
  printDoublesSinglesMode,
} from "../../utility/doublesSinglesSetup";
import { DOUBLES_SINGLES } from "../../utility/signupSheets/signupSheetModel";
import {
  EVENT_ONLINE,
  SQUAD_ONLINE,
  compatibleSquadsForEvent,
  eventOnlineStatus,
  eventsBySquadFromRows,
  isDoublesEvent,
  isSinglesEvent,
  onlineSignupHasOpenPath,
  shouldListSignupEvent,
  squadOnlineStatus,
} from "../../utility/signupAvailability";
import {
  countOpenCartLines,
  createCartLine,
  signupAlreadyOnEventSquad,
} from "../../utility/signupCart";
import { fetchMySignups, invokeCreateSignup } from "../../utility/submitSignup";
import { uploadSignupAverageProof } from "../../utility/signupAverageProof";
import { listBracketProducts } from "../../utility/brackets/api";
import {
  clampTicketCount,
  countsByBowlerPayload,
  productsEnabledForSquad,
  ticketCost,
} from "../../utility/brackets/entryTickets";
import {
  ActionButtons,
  EntryAccountCard,
  EntryAverageProofCard,
  EntryBowlersCard,
  EntryBracketsCard,
  EntrySidepotsCard,
  EventsSelectionCard,
  SquadSelectionCard,
  ThisEntrySummary,
  bowlerSlotsForEvent,
  formatPhoneNumber,
} from "./SignupLanding/index";
import { sanitizePhone, sanitizeUsbcId } from "../../utils/sanitization";
import { useTournamentId, useTournamentSlug } from "../../context/TournamentRouteContext";
import { tournamentPath } from "../../utility/tournamentUrls";
import {
  saveSignupDraft,
  loadSignupDraft,
  clearSignupDraft,
  restoreDraftToState,
  signupDraftInputsReady,
  withAccountPrimary,
} from "../../utility/signupDraft";

const mapExistingSignups = (signups = [], tournamentId) =>
  signups
    .filter((signup) => {
      const status = signup.status;
      if (status === "cancelled" || status === "denied") return false;
      const tid =
        signup.tournament?.id ||
        signup.tournamentEvents?.tournaments?.id ||
        signup.tournamentID;
      return !tournamentId || tid === tournamentId;
    })
    .map((signup) => {
      const bowlers = signup.signupBowlers || signup.tournamentSignupBowlers || [];
      const [primary, ...rest] = bowlers;
      return {
        eventID: signup.tournamentEvents?.id || signup.eventID,
        squadID: signup.squadId || signup.squadID,
        primaryBowler: {
          name: primary?.name,
          email: primary?.email,
          phone: primary?.phone,
          usbcId: primary?.usbcId || primary?.usbcID,
        },
        teammates: rest.map((bowler) => ({
          name: bowler.name,
          email: bowler.email,
          phone: bowler.phone,
          usbcId: bowler.usbcId || bowler.usbcID,
        })),
        waitlisted: signup.status === "waitlisted",
        paymentStatus: signup.paymentStatus,
      };
    });

const firstPrimaryValue = (signups, key) => {
  for (const signup of signups || []) {
    const value = signup?.primaryBowler?.[key];
    if (String(value || "").trim()) return value;
  }
  return "";
};

const knownPrimaryBowler = ({ profile, user, signups = [] } = {}) => ({
  name: String(
    profile?.full_name ||
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.user_metadata?.display_name ||
      ""
  ).trim(),
  email: String(user?.email || "").trim(),
  phone: formatPhoneNumber(
    profile?.phone_number ||
      firstPrimaryValue(signups, "phone") ||
      user?.user_metadata?.phone_number ||
      ""
  ),
  usbcId: sanitizeUsbcId(
    profile?.usbcId ||
      profile?.usbcID ||
      firstPrimaryValue(signups, "usbcId") ||
      ""
  ),
});

const SignupLanding = () => {
  const tournamentId = useTournamentId();
  const tournamentSlug = useTournamentSlug();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [tournament, setTournament] = useState(null);
  const [events, setEvents] = useState([]);
  const [squads, setSquads] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedSquad, setSelectedSquad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [formAnchor, setFormAnchor] = useState(null);
  const [formScrollTick, setFormScrollTick] = useState(0);

  useEffect(() => {
    if (!formScrollTick) return;
    const node = document.querySelector("[data-signup-error]");
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "center" });
    const field = node.matches("input, textarea, select")
      ? node
      : node.querySelector("input, textarea, select");
    field?.focus({ preventScroll: true });
  }, [formScrollTick]);
  const [signupSettings, setSignupSettings] = useState(
    getDefaultSignupSettings()
  );
  const [occupancyBySquad, setOccupancyBySquad] = useState({});
  const [doublesSingles, setDoublesSingles] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotErrors, setSlotErrors] = useState([]);
  const [sidepotEvents, setSidepotEvents] = useState([]);
  const [selectedSidepots, setSelectedSidepots] = useState({});
  const [existingSignups, setExistingSignups] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const [eventsBySquad, setEventsBySquad] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [accountProfile, setAccountProfile] = useState(null);
  const [averageProofFile, setAverageProofFile] = useState(null);
  const [averageProofError, setAverageProofError] = useState(null);
  const [bracketProducts, setBracketProducts] = useState([]);
  const [selectedBracketTickets, setSelectedBracketTickets] = useState({});
  const [draftRestored, setDraftRestored] = useState(false);
  const [draftInputsReady, setDraftInputsReady] = useState(false);

  const availabilityOptions = useMemo(
    () => ({
      events,
      eventsBySquad,
      doublesSingles,
      occupancyBySquad,
      waitlistEnabled: Boolean(signupSettings.waitlistEnabled),
      settings: signupSettings,
      squads,
    }),
    [events, eventsBySquad, doublesSingles, occupancyBySquad, signupSettings, squads]
  );

  const primaryEmail = (user?.email || slots[0]?.email || "").trim();
  const accountName = (
    accountProfile?.full_name ||
    user?.user_metadata?.full_name ||
    user?.user_metadata?.name ||
    user?.user_metadata?.display_name ||
    ""
  ).trim();

  useEffect(() => {
    if (!user) {
      setAccountProfile(null);
      return;
    }
    let cancelled = false;
    const loadProfile = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, phone_number, usbcId")
        .eq("id", user.id)
        .maybeSingle();
      if (!cancelled) setAccountProfile(data || {});
    };
    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const slotsRef = useRef(slots);
  const sidepotsRef = useRef(selectedSidepots);
  const bracketTicketsRef = useRef(selectedBracketTickets);
  slotsRef.current = slots;
  sidepotsRef.current = selectedSidepots;
  bracketTicketsRef.current = selectedBracketTickets;

  useLayoutEffect(() => {
    const currentSlots = slotsRef.current;
    if (!currentSlots.length) return;
    // Primary is the signed-in account. USBC, average, phone, and teammates stay on the draft.
    const applied = withAccountPrimary(
      currentSlots,
      knownPrimaryBowler({
        profile: accountProfile,
        user,
        signups: existingSignups,
      }),
      {
        requirePhone: signupSettings.requirePhone,
        requireUSBCID: signupSettings.requireUSBCID,
      },
      {
        selectedSidepots: sidepotsRef.current,
        selectedBracketTickets: bracketTicketsRef.current,
      }
    );
    if (!applied) return;
    setSlots(applied.slots);
    if (applied.selectedSidepots !== sidepotsRef.current) {
      setSelectedSidepots(applied.selectedSidepots);
    }
    if (applied.selectedBracketTickets !== bracketTicketsRef.current) {
      setSelectedBracketTickets(applied.selectedBracketTickets);
    }
  }, [
    user,
    accountProfile,
    existingSignups,
    signupSettings.requirePhone,
    signupSettings.requireUSBCID,
    slots.length,
  ]);

  const fetchTournamentData = useCallback(async () => {
    setDraftInputsReady(false);
    try {
      setLoading(true);
      setError(null);

      const { data: tournamentData, error: tournamentError } = await supabase
        .from("tournaments")
        .select("*")
        .eq("id", tournamentId)
        .single();

      if (tournamentError) throw tournamentError;
      setTournament(tournamentData);

      const { data: eventsData, error: eventsError } = await supabase
        .from("tournamentEvents")
        .select("*")
        .eq("tournamentID", tournamentId)
        .eq("isPrimary", true)
        .eq("isDeleted", false);

      if (eventsError) throw eventsError;

      const eventIds = (eventsData || []).map((event) => event.id);
      let eventFeesData = [];
      if (eventIds.length) {
        const { data, error: eventFeesError } = await supabase
          .from("eventFee")
          .select(
            `
          eventsID, 
          amount,
          fees (
            feeName
          )
        `
          )
          .in("eventsID", eventIds);
        if (eventFeesError) throw eventFeesError;
        eventFeesData = data || [];
      }

      const eventsWithFees = (eventsData || []).map((event) => {
        const fees = eventFeesData.filter((fee) => fee.eventsID === event.id);
        const totalFee = fees.reduce(
          (sum, fee) => addCurrency(sum, fee.amount || 0),
          0
        );
        return {
          ...event,
          entryFee: totalFee > 0 ? totalFee : 50,
        };
      });

      setEvents(eventsWithFees);

      const { data: squadsData, error: squadsError } = await supabase
        .from("squads")
        .select(
          `
          *,
          bowlingCenters (
            id,
            centerName,
            stateShort
          )
        `
        )
        .eq("tournamentID", tournamentId)
        .eq("isDeleted", false)
        .order("squadDate", { ascending: true });

      if (squadsError) throw squadsError;
      setSquads(squadsData || []);

      let squadEventsSettled = !squadsData?.length;
      if (squadsData?.length) {
        const { data: occupancyRows, error: occupancyError } =
          await supabase.rpc("get_squad_occupancy", {
            p_squad_ids: squadsData.map((squad) => squad.id),
          });
        if (occupancyError) {
          console.warn("Could not load squad occupancy:", occupancyError);
        } else {
          setOccupancyBySquad(occupancyBySquadId(occupancyRows || []));
        }

        const { data: eventRows, error: eventError } = await supabase
          .from("squadEvents")
          .select("squadID, eventsID")
          .in(
            "squadID",
            squadsData.map((squad) => squad.id)
          );
        if (eventError) {
          console.warn("Could not load squad events:", eventError);
          setEventsBySquad({});
        } else {
          setEventsBySquad(eventsBySquadFromRows(eventRows || []));
          squadEventsSettled = true;
        }
      }

      const [tournamentSettings, doublesRow, products] = await Promise.all([
        fetchTournamentSignupSettings(tournamentId),
        fetchDoublesSinglesSetup(tournamentId).catch(() => null),
        listBracketProducts(tournamentId).catch(() => []),
      ]);
      setSignupSettings(tournamentSettings);
      setDoublesSingles(doublesRow);
      setBracketProducts(productsEnabledForSquad(products));
      setDraftInputsReady(
        signupDraftInputsReady({
          squadEventsSettled,
          signupSettingsLoaded: true,
          // The promise has settled. null means it failed; the restore effect
          // will not delete a draft on that guess.
          doublesSetupSettled: true,
        })
      );

      setLoading(false);
    } catch (fetchError) {
      console.error("Error fetching tournament data:", fetchError);
      setError("Failed to load tournament data. Please try again.");
      setLoading(false);
    }
  }, [tournamentId]);

  useEffect(() => {
    fetchTournamentData();
  }, [fetchTournamentData]);

  // Restore draft when user signs in or page loads with authenticated user.
  // draftInputsReady flips only after squad events, signup settings, and
  // doubles setup have settled. Default settings are already truthy, so
  // checking signupSettings alone validates too early and can delete the draft.
  useEffect(() => {
    if (!user || draftRestored || !draftInputsReady) return;
    if (!events.length || !squads.length) return;

    const draft = loadSignupDraft(tournamentId);
    if (!draft) return;

    const restored = restoreDraftToState(draft, events, squads);
    if (restored.selectedEvent) {
      // Validate the restored event is still available
      const eventStatus = eventOnlineStatus(
        restored.selectedEvent,
        squads,
        {
          events,
          eventsBySquad,
          doublesSingles,
          occupancyBySquad,
          waitlistEnabled: Boolean(signupSettings.waitlistEnabled),
          settings: signupSettings,
          squads,
        }
      );
      
      // A failed doubles fetch leaves doublesSingles null, which signup
      // availability reads as "Singles follow Doubles". Do not delete the
      // draft on that guess. A loaded setup (including an empty row) is an object.
      const doublesSetupKnown = doublesSingles != null;

      // Only restore if event is still selectable
      if (eventStatus.status === EVENT_ONLINE.selectable) {
        setSelectedEvent(restored.selectedEvent);
        
        // Validate squad if present
        if (restored.selectedSquad) {
          const squadStatus = squadOnlineStatus(restored.selectedSquad, {
            occupancy: occupancyBySquad[restored.selectedSquad.id] || 0,
            waitlistEnabled: Boolean(signupSettings.waitlistEnabled),
            settings: signupSettings,
            squads,
            event: restored.selectedEvent,
            events,
            eventsBySquad,
            doublesSingles,
          });
          
          // Only restore squad if it's available
          if (squadStatus !== SQUAD_ONLINE.closed && squadStatus !== SQUAD_ONLINE.locked) {
            setSelectedSquad(restored.selectedSquad);
          }
        }
        
        const applied = withAccountPrimary(
          restored.slots,
          knownPrimaryBowler({
            profile: accountProfile,
            user,
            signups: existingSignups,
          }),
          {
            requirePhone: signupSettings.requirePhone,
            requireUSBCID: signupSettings.requireUSBCID,
          },
          {
            selectedSidepots: restored.selectedSidepots,
            selectedBracketTickets: restored.selectedBracketTickets,
          }
        );
        const restoredSlots = applied?.slots || restored.slots;
        const restoredSidepots = applied
          ? applied.selectedSidepots
          : restored.selectedSidepots;
        const restoredBrackets = applied
          ? applied.selectedBracketTickets
          : restored.selectedBracketTickets;
        if (restoredSlots && restoredSlots.length > 0) {
          setSlots(restoredSlots);
        }
        if (restoredSidepots && Object.keys(restoredSidepots).length > 0) {
          setSelectedSidepots(restoredSidepots);
        }
        if (restoredBrackets && Object.keys(restoredBrackets).length > 0) {
          setSelectedBracketTickets(restoredBrackets);
        }
        setDraftRestored(true);
      } else if (doublesSetupKnown) {
        // Loaded settings say this event can no longer be entered.
        clearSignupDraft(tournamentId);
        setDraftRestored(true);
      }
    } else if (doublesSingles != null) {
      clearSignupDraft(tournamentId);
      setDraftRestored(true);
    }
  }, [user, accountProfile, existingSignups, tournamentId, events, squads, draftRestored, draftInputsReady, eventsBySquad, doublesSingles, occupancyBySquad, signupSettings]);

  useEffect(() => {
    if (!user) {
      setExistingSignups([]);
      setCartCount(0);
      return;
    }
    let cancelled = false;
    const loadExisting = async () => {
      try {
        const data = await fetchMySignups();
        if (cancelled) return;
        const mapped = mapExistingSignups(
          data.activeSignups || data.signups || [],
          tournamentId
        );
        setExistingSignups(mapped);
        setCartCount(countOpenCartLines(data, tournamentId));
      } catch (loadError) {
        console.warn("Could not load existing signups:", loadError);
      }
    };
    loadExisting();
    return () => {
      cancelled = true;
    };
  }, [user, tournamentId]);

  useEffect(() => {
    if (!selectedSquad || !tournament) {
      setSidepotEvents([]);
      return;
    }
    let cancelled = false;
    const loadSidepots = async () => {
      const { data, error: sidepotError } = await supabase
        .from("tournamentEvents")
        .select(
          `
          *,
          eventFee (
            id,
            fees ( feeName ),
            amount
          )
        `
        )
        .eq("tournamentID", tournament.id)
        .eq("isPrimary", false)
        .eq("isDeleted", false)
        .or(`templateId.is.null,squadId.eq.${selectedSquad.id}`);
      if (cancelled) return;
      if (sidepotError) {
        console.warn("Could not load sidepots:", sidepotError);
        setSidepotEvents([]);
        return;
      }
      const processed = (data || []).map((event) => {
        const fees = event.eventFee || [];
        const totalEntryFee = fees.reduce(
          (sum, fee) => addCurrency(sum, fee.amount || 0),
          0
        );
        return { ...event, entryFee: totalEntryFee };
      });
      setSidepotEvents(processed);
    };
    loadSidepots();
    return () => {
      cancelled = true;
    };
  }, [selectedSquad, tournament]);

  const singlesEvent = events.find(isSinglesEvent);
  const doublesMode = printDoublesSinglesMode(doublesSingles);
  const showSinglesToggle =
    isDoublesEvent(selectedEvent) &&
    Boolean(singlesEvent) &&
    doublesMode === DOUBLES_SINGLES.optional;
  const everyoneBowlsSingles =
    isDoublesEvent(selectedEvent) &&
    Boolean(singlesEvent) &&
    doublesMode === DOUBLES_SINGLES.always;

  const squadIsAlreadyEntered = (event, squad) => {
    if (!event || !squad) return false;
    return signupAlreadyOnEventSquad(existingSignups, event.id, squad.id);
  };

  const getEventSignupStatus = (event) => {
    const result = eventOnlineStatus(event, squads, availabilityOptions);
    if (result.status === EVENT_ONLINE.selectable) {
      return { selectable: true };
    }
    if (result.status === EVENT_ONLINE.via_doubles) {
      return { selectable: false, viaDoubles: true, note: result.reason };
    }
    return {
      selectable: false,
      note: result.reason || "Signup closed",
    };
  };

  const listedEvents = events.filter((event) =>
    shouldListSignupEvent(event, squads, availabilityOptions)
  );
  const signupOpen = onlineSignupHasOpenPath(
    events,
    squads,
    availabilityOptions
  );

  useEffect(() => {
    if (!selectedEvent) return;
    const status = eventOnlineStatus(
      selectedEvent,
      squads,
      availabilityOptions
    );
    if (status.status !== EVENT_ONLINE.selectable) {
      setSelectedEvent(null);
      setSelectedSquad(null);
    }
  }, [selectedEvent, squads, availabilityOptions]);

  const getSquadStatus = (squad) =>
    squadOnlineStatus(squad, {
      occupancy: occupancyBySquad[squad.id] || 0,
      waitlistEnabled: Boolean(signupSettings.waitlistEnabled),
      settings: signupSettings,
      squads,
      event: selectedEvent,
      events,
      eventsBySquad,
      doublesSingles,
    });

  const handleEventSelect = (event) => {
    if (!getEventSignupStatus(event).selectable) return;
    setSelectedEvent(event);
    setSelectedSquad(null);
    const known = knownPrimaryBowler({
      profile: accountProfile,
      user,
      signups: existingSignups,
    });
    setSlots(
      bowlerSlotsForEvent(event, [
        {
          name: known.name,
          email: known.email,
          phone: signupSettings.requirePhone ? known.phone : "",
          usbcId: signupSettings.requireUSBCID ? known.usbcId : "",
        },
      ])
    );
    setSlotErrors([]);
    setSelectedSidepots({});
    setFormError(null);
    setFormAnchor(null);
  };

  // Save draft whenever key selections change
  useEffect(() => {
    if (!selectedEvent && !selectedSquad) return;
    
    saveSignupDraft(tournamentId, {
      selectedEvent,
      selectedSquad,
      slots,
      selectedSidepots,
      selectedBracketTickets,
    });
  }, [
    tournamentId,
    selectedEvent,
    selectedSquad,
    slots,
    selectedSidepots,
    selectedBracketTickets,
  ]);

  // Ensure draft is saved before user navigates away
  useEffect(() => {
    const handlePageHide = () => {
      // Flush draft immediately before page unload
      if (selectedEvent || selectedSquad) {
        saveSignupDraft(tournamentId, {
          selectedEvent,
          selectedSquad,
          slots,
          selectedSidepots,
          selectedBracketTickets,
        });
      }
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handlePageHide);

    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handlePageHide);
    };
  }, [tournamentId, selectedEvent, selectedSquad, slots, selectedSidepots, selectedBracketTickets]);

  const handleSquadSelect = (squad) => {
    const status = getSquadStatus(squad);
    if (status === SQUAD_ONLINE.full || status === SQUAD_ONLINE.closed || status === SQUAD_ONLINE.locked) {
      setFormError(status === SQUAD_ONLINE.full ? "This squad is full." : "Signup closed.");
      setFormAnchor("squad");
      setFormScrollTick((tick) => tick + 1);
      return;
    }
    setFormError(null);
    setFormAnchor(null);
    setSelectedSquad(squad);
  };

  const handleSlotChange = (index, field, value) => {
    setSlots((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
    setSlotErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const next = [...prev];
      next[index] = { ...next[index], [field]: null };
      return next;
    });
  };

  const handleSidepotToggle = (bowlerName, sidepotId, checked) => {
    setSelectedSidepots((prev) => ({
      ...prev,
      [bowlerName]: {
        ...(prev[bowlerName] || {}),
        [sidepotId]: checked,
      },
    }));
  };

  const handleBracketTicketChange = (bowlerName, productId, nextCount) => {
    setSelectedBracketTickets((prev) => ({
      ...prev,
      [bowlerName]: {
        ...(prev[bowlerName] || {}),
        [productId]: clampTicketCount(nextCount),
      },
    }));
  };

  const validateSlots = () => {
    const errors = slots.map((slot, index) => {
      const row = {};
      if (!String(slot.name || "").trim()) row.name = "Name is required";
      const emailValue =
        index === 0 ? slot.email || primaryEmail : slot.email;
      if (signupSettings.requireEmail && !String(emailValue || "").trim()) {
        row.email = "Email is required";
      } else if (emailValue) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(emailValue)) row.email = "Enter a valid email";
      }
      if (signupSettings.requirePhone && !String(slot.phone || "").trim()) {
        row.phone = "Phone is required";
      }
      if (signupSettings.requireUSBCID && !String(slot.usbcId || "").trim()) {
        row.usbcId = "USBC ID is required";
      }
      if (signupSettings.requireAverage && slot.average === "") {
        row.average = "Average is required";
      }
      return row;
    });
    const hasError = errors.some((row) => Object.keys(row).length);
    setSlotErrors(errors);
    return !hasError;
  };

  const sidepotAmountsForSlots = (people) => {
    const amounts = {};
    people.forEach((person) => {
      const picks = selectedSidepots[person.name] || {};
      const byEvent = {};
      sidepotEvents.forEach((sidepot) => {
        if (picks[sidepot.id]) byEvent[sidepot.id] = sidepot.entryFee || 0;
      });
      if (Object.keys(byEvent).length) amounts[person.name] = byEvent;
    });
    return amounts;
  };

  const sidepotTotal = useMemo(() => {
    let total = 0;
    Object.values(selectedSidepots).forEach((picks) => {
      sidepotEvents.forEach((sidepot) => {
        if (picks?.[sidepot.id]) total = addCurrency(total, sidepot.entryFee || 0);
      });
    });
    return total;
  }, [selectedSidepots, sidepotEvents]);

  const bracketTotal = useMemo(() => {
    if (!signupSettings.allowOnlineBrackets) return 0;
    return Object.values(selectedBracketTickets).reduce(
      (sum, counts) => addCurrency(sum, ticketCost(bracketProducts, counts)),
      0
    );
  }, [
    signupSettings.allowOnlineBrackets,
    selectedBracketTickets,
    bracketProducts,
  ]);

  const squadStatus = selectedSquad ? getSquadStatus(selectedSquad) : null;
  const waitlisted = squadStatus === SQUAD_ONLINE.waitlist;

  const handleAddToCart = async () => {
    setFormError(null);
    setFormAnchor(null);
    setSlotErrors([]);
    setAverageProofError(null);
    if (!user) {
      setFormError("Sign in under Account before you add to cart.");
      setFormAnchor("account");
      setFormScrollTick((tick) => tick + 1);
      return;
    }
    if (!selectedEvent || !selectedSquad) {
      setFormError("Pick an event and a squad.");
      setFormAnchor(selectedEvent ? "squad" : "event");
      setFormScrollTick((tick) => tick + 1);
      return;
    }
    if (
      eventOnlineStatus(selectedEvent, squads, availabilityOptions).status ===
      EVENT_ONLINE.via_doubles
    ) {
      setFormError("Add Singles on Doubles.");
      setFormAnchor("event");
      setFormScrollTick((tick) => tick + 1);
      return;
    }
    if (!validateSlots()) {
      setFormError("Fill in every bowler on this entry.");
      setFormAnchor("bowlers");
      setFormScrollTick((tick) => tick + 1);
      return;
    }
    if (averageProofIsRequired(signupSettings) && !averageProofFile) {
      setAverageProofError("Upload a photo or PDF for average verification.");
      setFormError("Upload a photo or PDF for average verification.");
      setFormAnchor("proof");
      setFormScrollTick((tick) => tick + 1);
      return;
    }

    const [primaryBowler, ...teammates] = slots.map((slot, index) => ({
      name: slot.name.trim(),
      email: index === 0 ? slot.email || primaryEmail : slot.email,
      phone: slot.phone,
      usbcId: slot.usbcId,
      average: slot.average,
      alsoBowlSingles: slot.alsoBowlSingles,
    }));

    const mainLine = createCartLine({
      event: selectedEvent,
      squad: selectedSquad,
      primaryBowler,
      teammates,
      sidepots: sidepotAmountsForSlots([primaryBowler, ...teammates]),
      waitlisted,
    });

    const extraLines = [];
    const addSinglesFor = (bowler) => {
      if (!singlesEvent) return;
      extraLines.push(
        createCartLine({
          event: singlesEvent,
          squad: selectedSquad,
          primaryBowler: bowler,
          teammates: [],
          sidepots: {},
          waitlisted,
          companionOf: mainLine.id,
        })
      );
    };

    if (everyoneBowlsSingles) {
      [primaryBowler, ...teammates].forEach(addSinglesFor);
    } else if (showSinglesToggle) {
      [primaryBowler, ...teammates]
        .filter((bowler) => bowler.alsoBowlSingles)
        .forEach(addSinglesFor);
    }

    setSubmitting(true);
    try {
      if (
        user &&
        (primaryBowler.name || primaryBowler.phone || primaryBowler.usbcId)
      ) {
        const profileUpdates = {
          id: user.id,
          updated_at: new Date().toISOString(),
        };
        if (primaryBowler.name && !accountName) {
          profileUpdates.full_name = primaryBowler.name.trim();
        }
        if (primaryBowler.phone) {
          profileUpdates.phone_number = sanitizePhone(primaryBowler.phone);
        }
        if (primaryBowler.usbcId) {
          profileUpdates.usbcId = sanitizeUsbcId(primaryBowler.usbcId);
        }
        if (
          profileUpdates.full_name ||
          profileUpdates.phone_number ||
          profileUpdates.usbcId
        ) {
          const { error: profileError } = await supabase
            .from("profiles")
            .upsert(profileUpdates, { onConflict: "id" });
          if (!profileError) {
            setAccountProfile((prev) => ({
              ...(prev || {}),
              ...(profileUpdates.full_name
                ? { full_name: profileUpdates.full_name }
                : {}),
              ...(profileUpdates.phone_number
                ? { phone_number: profileUpdates.phone_number }
                : {}),
              ...(profileUpdates.usbcId
                ? { usbcId: profileUpdates.usbcId }
                : {}),
            }));
          }
        }
      }
      const lines = [mainLine, ...extraLines];
      let averageProofPath = null;
      if (averageProofFile) {
        averageProofPath = await uploadSignupAverageProof({
          tournamentId,
          userId: user.id,
          file: averageProofFile,
        });
      }
      for (const line of lines) {
        await invokeCreateSignup({
          email: user.email || primaryBowler.email,
          eventID: line.event.id,
          squadID: line.squad.id,
          primaryBowler: line.primaryBowler,
          teammates: line.teammates,
          selectedSidepots:
            line === mainLine ? selectedSidepots : {},
          averageProofPath,
          bracketTickets:
            line === mainLine
              ? countsByBowlerPayload(selectedBracketTickets)
              : {},
        });
      }
      setSelectedEvent(null);
      setSelectedSquad(null);
      setSlots([]);
      setSelectedSidepots({});
      setSelectedBracketTickets({});
      setAverageProofFile(null);
      setAverageProofError(null);
      
      // Clear the draft after successful submission
      clearSignupDraft(tournamentId);
      
      navigate(tournamentPath(tournamentSlug, `/signups/cart`));
    } catch (submitError) {
      setFormError(
        submitError.message || "Could not add this entry. Try again."
      );
      setFormAnchor("submit");
      setFormScrollTick((tick) => tick + 1);
    } finally {
      setSubmitting(false);
    }
  };

  const signupSeo = <TournamentSubpageHead page="signups" />;

  if (loading) {
    return (
      <>
        {signupSeo}
        <TournamentPageSkeleton
          title="Signups"
          subtitle="Pick an event, squad, and bowlers."
          cards={4}
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        {signupSeo}
        <AlertBox type="error" title="Error" message={error} />
      </>
    );
  }

  if (!tournament) {
    return (
      <>
        {signupSeo}
        <AlertBox
          type="error"
          title="Tournament Not Found"
          message="The requested tournament could not be found."
        />
      </>
    );
  }

  const availableSquads = compatibleSquadsForEvent(
    selectedEvent,
    squads,
    availabilityOptions
  );

  const canSubmit = Boolean(selectedEvent && selectedSquad && user) && !submitting;
  const submitHint = user
    ? selectedEvent
      ? selectedSquad
        ? null
        : "Choose a squad to continue."
      : "Choose an event to continue."
    : "Sign in to add this entry to your cart.";
  const submitLabel = submitting
    ? "Adding…"
    : waitlisted
      ? "Join waitlist"
      : "Add to cart";

  const breadcrumbItems = [
    { label: "Tournaments", path: "/tournaments" },
    {
      label: tournament.name || "Tournament",
      path: tournamentPath(tournamentSlug),
    },
    { label: "Signups" },
  ];

  return (
    <>
      {signupSeo}
      <div className="flex justify-center mb-3 sm:mb-4 px-4 sm:px-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="space-y-6">
        {formError ? (
          <AlertBox type="error" title="Please fix this" message={formError} />
        ) : null}

        {!signupOpen ? (
          <div className="card-board border-l-4 border-l-brand-charcoal/30 p-4">
            <h2 className="text-sm font-display font-bold text-brand-charcoal">
              Signup closed
            </h2>
            <p className="text-sm text-brand-muted mt-1">
              No squads are open for online signup.
            </p>
          </div>
        ) : null}

        <div className="lg:grid lg:grid-cols-3 lg:gap-8 lg:items-start">
          <aside className="order-last lg:order-none lg:col-start-3 lg:row-start-1 mt-6 lg:mt-0 lg:sticky lg:top-3">
            <ThisEntrySummary
              event={selectedEvent}
              squad={selectedSquad}
              slots={slots}
              sidepotTotal={sidepotTotal}
              bracketTotal={bracketTotal}
              waitlisted={waitlisted}
              cartCount={cartCount}
              onViewCart={() =>
                navigate(tournamentPath(tournamentSlug, `/signups/cart`))
              }
            />
          </aside>
          <div className="lg:col-span-2 lg:col-start-1 lg:row-start-1">
            <EventsSelectionCard
              events={listedEvents}
              selectedEvent={selectedEvent}
              onEventSelect={handleEventSelect}
              getEventSignupStatus={getEventSignupStatus}
              error={formAnchor === "event" ? formError : null}
              pageHeader={
                <div className="bg-brand-charcoal text-brand-cream px-6 py-4">
                  <h1 className="font-display font-bold text-xl text-brand-cream tracking-tight">
                    Signups
                  </h1>
                  <p className="text-sm text-brand-mist mt-1">
                    Pick an event, squad, and bowlers.
                  </p>
                </div>
              }
            />
            <SquadSelectionCard
              selectedEvent={selectedEvent}
              availableSquads={availableSquads}
              selectedSquad={selectedSquad}
              onSquadSelect={handleSquadSelect}
              occupancyBySquad={occupancyBySquad}
              signupSettings={signupSettings}
              availabilityOptions={availabilityOptions}
              getSquadStatus={getSquadStatus}
              alreadyEnteredOnSquad={(squad) =>
                squadIsAlreadyEntered(selectedEvent, squad)
              }
              error={formAnchor === "squad" ? formError : null}
            />
            <EntryBowlersCard
              event={selectedEvent}
              slots={slots}
              errors={slotErrors}
              signupSettings={signupSettings}
              showSinglesToggle={showSinglesToggle}
              everyoneBowlsSingles={everyoneBowlsSingles}
              signedIn={Boolean(user)}
              primaryAccountEmail={primaryEmail}
              primaryAccountName={accountName}
              onSlotChange={handleSlotChange}
            />
            <EntryAverageProofCard
              show={Boolean(
                selectedEvent && averageProofIsOffered(signupSettings)
              )}
              required={averageProofIsRequired(signupSettings)}
              file={averageProofFile}
              error={averageProofError}
              directorInstructions={directorAverageVerificationInstructions(
                signupSettings
              )}
              onFileChange={(nextFile, nextError) => {
                setAverageProofFile(nextFile);
                setAverageProofError(nextError);
              }}
            />
            <EntrySidepotsCard
              sidepotEvents={sidepotEvents}
              slots={slots}
              selectedSidepots={selectedSidepots}
              onSidepotToggle={handleSidepotToggle}
            />
            <EntryBracketsCard
              products={
                signupSettings.allowOnlineBrackets ? bracketProducts : []
              }
              slots={slots}
              selectedTickets={selectedBracketTickets}
              onTicketChange={handleBracketTicketChange}
              squadId={selectedSquad?.id}
            />
            <EntryAccountCard
              user={user}
              email={primaryEmail}
              notice={formAnchor === "account" ? formError : null}
            />
            {squadIsAlreadyEntered(selectedEvent, selectedSquad) ? (
              <p className="text-sm text-brand-charcoal mb-4">
                You already have an entry on this squad. You can still add
                another.
              </p>
            ) : null}
            <ActionButtons
              canSubmit={canSubmit}
              submitLabel={submitLabel}
              onAddToCart={handleAddToCart}
              onBackToTournament={() => navigate(tournamentPath(tournamentSlug))}
              error={formAnchor === "submit" ? formError : null}
              hint={submitHint}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default SignupLanding;
